import type { ConnectedThemeEdge, ConnectedThemeNode } from "../data/monitoring";

/** Keep the graph readable while retaining all qualifying pairs in chart data. */
export function pruneThemeNetworkEdges(edges: ConnectedThemeEdge[], perThemeLimit = 2): ConnectedThemeEdge[] {
  const ranked = new Map<string, ConnectedThemeEdge[]>();
  for (const edge of edges) {
    for (const theme of [edge.source_theme, edge.target_theme]) {
      const current = ranked.get(theme) ?? [];
      current.push(edge);
      ranked.set(theme, current);
    }
  }
  const keep = new Set<string>();
  for (const themeEdges of ranked.values()) {
    themeEdges
      .sort((left, right) => right.current_lift - left.current_lift || right.current_shared_users - left.current_shared_users || left.source_theme.localeCompare(right.source_theme) || left.target_theme.localeCompare(right.target_theme))
      .slice(0, perThemeLimit)
      .forEach((edge) => keep.add(`${edge.source_theme}::${edge.target_theme}`));
  }
  return edges.filter((edge) => keep.has(`${edge.source_theme}::${edge.target_theme}`));
}

/** Select the small set of links that can support a clear client narrative. */
export function selectNarrativeNetworkEdges(nodes: ConnectedThemeNode[], edges: ConnectedThemeEdge[]): ConnectedThemeEdge[] {
  const newThemes = new Set(nodes.filter((node) => node.baseline_users === 0).map((node) => node.theme));
  const sortStrongest = (left: ConnectedThemeEdge, right: ConnectedThemeEdge) => right.current_lift - left.current_lift || right.current_shared_users - left.current_shared_users || left.source_theme.localeCompare(right.source_theme) || left.target_theme.localeCompare(right.target_theme);
  const keep = new Set<string>();
  for (const theme of newThemes) {
    edges
      .filter((edge) => edge.source_theme === theme || edge.target_theme === theme)
      .sort(sortStrongest)
      .slice(0, 2)
      .forEach((edge) => keep.add(`${edge.source_theme}::${edge.target_theme}`));
  }
  for (const edge of edges) {
    if (!edge.is_new && edge.current_lift >= 1.75 && edge.current_shared_users >= 220 && (edge.lift_change ?? -Infinity) >= 0) {
      keep.add(`${edge.source_theme}::${edge.target_theme}`);
    }
  }
  return edges.filter((edge) => keep.has(`${edge.source_theme}::${edge.target_theme}`));
}

const edgeKey = (edge: ConnectedThemeEdge) => `${edge.source_theme}::${edge.target_theme}`;
const edgeRank = (left: ConnectedThemeEdge, right: ConnectedThemeEdge) => right.current_lift - left.current_lift || right.current_shared_users - left.current_shared_users || left.source_theme.localeCompare(right.source_theme) || left.target_theme.localeCompare(right.target_theme);
export type ThemeCommunity = { label: string; themes: string[]; priority: number };

/** Scale the full qualifying edge set so weaker links remain contextual. */
export function networkEdgeVisualWeight(lift: number, minimumLift: number, maximumLift: number): number {
  const span = Math.max(maximumLift - minimumLift, .01);
  const normalized = Math.max(0, Math.min(1, (lift - minimumLift) / span));
  return .35 + normalized ** 1.55 * 6.2;
}

/** Blend a community color toward neutral gray as its selected-theme relevance falls. */
export function networkNodeColor(color: string | undefined, relevance: number): string {
  const clamped = Math.max(0, Math.min(1, relevance));
  if (!color) return "#aeb8c9";
  const source = color.slice(1).match(/.{2}/g)?.map((part) => Number.parseInt(part, 16));
  const neutral = [174, 184, 201];
  if (!source || source.length !== 3) return color;
  const channel = (index: number) => Math.round(neutral[index] + (source[index] - neutral[index]) * clamped).toString(16).padStart(2, "0");
  return `#${channel(0)}${channel(1)}${channel(2)}`;
}

/** Color each edge relative to the visible connection-strength range. */
export function networkEdgeColor(lift: number, minimumLift: number, maximumLift: number): string {
  const span = Math.max(maximumLift - minimumLift, .01);
  return networkNodeColor("#4777d1", (lift - minimumLift) / span);
}

/**
 * Build an adaptive network backbone. Mutual local ties make the core; any
 * isolated theme then attaches through the strongest relatively competitive,
 * least-connected alternative so hubs cannot absorb the full map.
 */
export function buildRelativeBackbone(nodes: ConnectedThemeNode[], edges: ConnectedThemeEdge[], relativeFloor = .75): ConnectedThemeEdge[] {
  const incident = new Map(nodes.map((node) => [node.theme, edges.filter((edge) => edge.source_theme === node.theme || edge.target_theme === node.theme).sort(edgeRank)]));
  const localRank = new Map<string, Map<string, { rank: number; ratio: number }>>();
  for (const [theme, themeEdges] of incident) {
    const strongest = themeEdges[0]?.current_lift ?? 1;
    localRank.set(theme, new Map(themeEdges.map((edge, rank) => [edgeKey(edge), { rank, ratio: edge.current_lift / strongest }])));
  }
  const selected = new Set<string>();
  const degree = new Map(nodes.map((node) => [node.theme, 0]));
  const add = (edge: ConnectedThemeEdge) => {
    if (selected.has(edgeKey(edge))) return;
    selected.add(edgeKey(edge));
    degree.set(edge.source_theme, (degree.get(edge.source_theme) ?? 0) + 1);
    degree.set(edge.target_theme, (degree.get(edge.target_theme) ?? 0) + 1);
  };
  for (const edge of [...edges].sort(edgeRank)) {
    const source = localRank.get(edge.source_theme)?.get(edgeKey(edge));
    const target = localRank.get(edge.target_theme)?.get(edgeKey(edge));
    if (source && target && source.rank < 2 && target.rank < 2 && source.ratio >= relativeFloor && target.ratio >= relativeFloor) add(edge);
  }
  for (const node of [...nodes].sort((left, right) => right.priority_index - left.priority_index || left.theme.localeCompare(right.theme))) {
    if ((degree.get(node.theme) ?? 0) > 0) continue;
    const candidates = (incident.get(node.theme) ?? []).filter((edge) => {
      const ratio = localRank.get(node.theme)?.get(edgeKey(edge))?.ratio ?? 0;
      return ratio >= relativeFloor;
    });
    const choices = candidates.length ? candidates : incident.get(node.theme) ?? [];
    const best = [...choices].sort((left, right) => {
      const other = (edge: ConnectedThemeEdge) => edge.source_theme === node.theme ? edge.target_theme : edge.source_theme;
      const leftRatio = localRank.get(node.theme)?.get(edgeKey(left))?.ratio ?? 0;
      const rightRatio = localRank.get(node.theme)?.get(edgeKey(right))?.ratio ?? 0;
      const leftScore = leftRatio / (1 + (degree.get(other(left)) ?? 0));
      const rightScore = rightRatio / (1 + (degree.get(other(right)) ?? 0));
      return rightScore - leftScore || edgeRank(left, right);
    })[0];
    if (best) add(best);
  }
  return edges.filter((edge) => selected.has(edgeKey(edge)));
}

export function deriveThemeCommunities(nodes: ConnectedThemeNode[], edges: ConnectedThemeEdge[]): ThemeCommunity[] {
  const adjacency = new Map(nodes.map((node) => [node.theme, new Set<string>()]));
  for (const edge of edges) { adjacency.get(edge.source_theme)?.add(edge.target_theme); adjacency.get(edge.target_theme)?.add(edge.source_theme); }
  const priority = new Map(nodes.map((node) => [node.theme, node.priority_index]));
  const visited = new Set<string>();
  const groups: string[][] = [];
  for (const node of nodes) {
    if (visited.has(node.theme)) continue;
    const stack = [node.theme]; const group: string[] = []; visited.add(node.theme);
    while (stack.length) {
      const current = stack.pop()!; group.push(current);
      for (const neighbor of adjacency.get(current) ?? []) if (!visited.has(neighbor)) { visited.add(neighbor); stack.push(neighbor); }
    }
    groups.push(group);
  }
  return groups
    .map((themes) => {
      const ranked = [...themes].sort((left, right) => (priority.get(right) ?? 0) - (priority.get(left) ?? 0) || left.localeCompare(right));
      return { themes: ranked, priority: ranked.reduce((total, theme) => total + (priority.get(theme) ?? 0), 0) };
    })
    .sort((left, right) => right.priority - left.priority || left.themes[0].localeCompare(right.themes[0]))
    .map((community, index) => ({ ...community, label: `Macro Theme ${String(index + 1).padStart(2, "0")}` }));
}

/** Connect every macro theme with a degree-balanced bridge tree. */
export function selectMacroBridges(communities: ThemeCommunity[], edges: ConnectedThemeEdge[]): ConnectedThemeEdge[] {
  const communityByTheme = new Map(communities.flatMap((community, index) => community.themes.map((theme) => [theme, index])));
  const candidates = edges.filter((edge) => communityByTheme.get(edge.source_theme) !== communityByTheme.get(edge.target_theme));
  const parent = communities.map((_, index) => index);
  const find = (index: number): number => parent[index] === index ? index : (parent[index] = find(parent[index]));
  const bridgeDegree = new Map<string, number>();
  const selected = new Set<string>();
  while (selected.size < Math.max(communities.length - 1, 0)) {
    const bridge = candidates
      .filter((edge) => {
        const sourceGroup = communityByTheme.get(edge.source_theme);
        const targetGroup = communityByTheme.get(edge.target_theme);
        return sourceGroup !== undefined && targetGroup !== undefined && find(sourceGroup) !== find(targetGroup);
      })
      .sort((left, right) => {
        const leftScore = left.current_lift / (1 + (bridgeDegree.get(left.source_theme) ?? 0) + (bridgeDegree.get(left.target_theme) ?? 0));
        const rightScore = right.current_lift / (1 + (bridgeDegree.get(right.source_theme) ?? 0) + (bridgeDegree.get(right.target_theme) ?? 0));
        return rightScore - leftScore || edgeRank(left, right);
      })[0];
    if (!bridge) break;
    selected.add(edgeKey(bridge));
    bridgeDegree.set(bridge.source_theme, (bridgeDegree.get(bridge.source_theme) ?? 0) + 1);
    bridgeDegree.set(bridge.target_theme, (bridgeDegree.get(bridge.target_theme) ?? 0) + 1);
    const sourceGroup = communityByTheme.get(bridge.source_theme)!;
    const targetGroup = communityByTheme.get(bridge.target_theme)!;
    parent[find(sourceGroup)] = find(targetGroup);
  }
  return edges.filter((edge) => selected.has(edgeKey(edge)));
}

/**
 * Reserve a distinct area for every macro theme before placing its nodes.
 * Cross-macro bridges still appear in the graph, but they cannot collapse
 * otherwise unrelated clusters into the same visual space.
 */
export function buildStaticThemeNetworkLayout(themes: string[], communities: string[][]): Record<string, { x: number; y: number }> {
  const names = [...themes].sort((left, right) => left.localeCompare(right));
  if (!names.length) return {};
  const assigned = new Set(communities.flat());
  const groups = [...communities.filter((group) => group.length), ...names.filter((name) => !assigned.has(name)).map((name) => [name])];
  const columns = Math.min(2, Math.max(groups.length, 1));
  const rows = Math.ceil(groups.length / columns);
  const positions: Record<string, { x: number; y: number }> = {};
  const localOffsets = (count: number): { x: number; y: number }[] => {
    if (count === 1) return [{ x: 0, y: 0 }];
    if (count === 2) return [{ x: -88, y: 0 }, { x: 88, y: 0 }];
    if (count === 3) return [{ x: 0, y: -78 }, { x: -100, y: 64 }, { x: 100, y: 64 }];
    return Array.from({ length: count }, (_, index) => {
      const angle = -Math.PI / 2 + (index * Math.PI * 2) / count;
      return { x: Math.cos(angle) * 118, y: Math.sin(angle) * 88 };
    });
  };
  groups.forEach((group, index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);
    const centerX = (column - (columns - 1) / 2) * 520;
    const centerY = (row - (rows - 1) / 2) * 300;
    const offsets = localOffsets(group.length);
    [...group].sort((left, right) => left.localeCompare(right)).forEach((theme, nodeIndex) => {
      const offset = offsets[nodeIndex];
      positions[theme] = { x: centerX + offset.x, y: centerY + offset.y };
    });
  });
  return positions;
}
